import React from "react";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { EventStatus } from "../../../../models/enums/eventStatus.enum";
import {
  mapToTrainingTypeString,
  mapTrainingTypeToArticleWithNoun,
  TrainingType,
} from "../../../../models/enums/trainingType.enum";
import { EventFormData } from "../../../../models/eventData";
import { AppState } from "../../../../redux";
import CustomTabs from "../../../theming/tabs/CustomTabs";
import { BasicInformation } from "../shared/basic-information/BasicInformation";
import { Content } from "../shared/content/Content";
import { Dates } from "../shared/Dates";
import { Documents } from "../shared/Documents";
import { Miscellaneous } from "../shared/misc/Miscellaneous";
import { Participants } from "../shared/participants/Participants";
import { SpeakersView } from "../shared/SpeakersView";
import { useTabPersistence } from "../../../../hooks/useTabPersistence";

/**
 * @interface EventTabsProps
 */
interface EventTabsProps {
  viewMode: "createEvent" | "editEvent" | "template";
  typeOfTrainingToBeCreated: TrainingType;
  eventId?: string;
}

/**
 * Component for managing the tabs of an event.
 * @param {EventTabsProps} props - The props of the component.
 * @returns JSX.Element
 */
const EventTabs: React.FC<EventTabsProps> = (props) => {
  const { values, setFieldValue } = useFormikContext<EventFormData>();
  const templates = useSelector((state: AppState) => state.eventTemplates);
  const editEventState = useSelector((state: AppState) => state.event.editEvent);
  const { typeOfTrainingToBeCreated, eventId, viewMode } = props;
  const { activeTab, handleTabChange } = useTabPersistence({ defaultTab: 0 });

  const defaultTabs = [
    {
      label: "Basisinformationen",
      content: (
        <BasicInformation typeOfTrainingToBeCreated={typeOfTrainingToBeCreated} />
      ),
    },
    {
      label: "Inhalt",
      content: <Content />,
    },
  ];

  const createEventTabs = [
    {
      label: "Referenten",
      content: <SpeakersView template_mode={templates.eventTemplateMode} />,
    },
    {
      label: "Termine",
      content: <Dates />,
    },
    {
      label: "Material",
      content: (
        <Documents
          template_id={templates.eventTemplate.id}
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
        />
      ),
    },
    {
      label: "Sonstiges",
      content: (
        <Miscellaneous
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
          eventId={eventId ? eventId : "0"}
        />
      ),
      disabled: true, // temporary "fix" to prevent discount errors in create event
    },
  ];

  const editEventTabs = [
    {
      label: "Referenten",
      content: <SpeakersView />,
    },
    {
      label: "Termine",
      content: <Dates />,
    },
    {
      label: "Material",
      content: <Documents typeOfTrainingToBeCreated={typeOfTrainingToBeCreated} />,
    },
    {
      label: "Teilnehmer",
      content: (
        <Participants
          eventData={editEventState}
          trainingType={typeOfTrainingToBeCreated}
        />
      ),
      disabled: editEventState.status === EventStatus.NotVisible,
      disabledTooltip: `Der Reiter "Teilnehmer" ist nicht verfügbar, solange ${mapTrainingTypeToArticleWithNoun(
        typeOfTrainingToBeCreated,
        mapToTrainingTypeString(typeOfTrainingToBeCreated)
      )} nicht sichtbar ist.`,
    },
    {
      label: "Sonstiges",
      content: (
        <Miscellaneous
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
          eventId={eventId ? eventId : "0"}
        />
      ),
    },
  ];

  const templateTabs = [
    {
      label: "Referenten",
      content: <SpeakersView template_mode />,
    },
    {
      label: "Material",
      content: (
        <Documents template_mode typeOfTrainingToBeCreated={typeOfTrainingToBeCreated} />
      ),
    },
  ];

  const elearningTabs = [
    {
      label: "Material",
      content: (
        <Documents
          template_id={templates.eventTemplate.id}
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
        />
      ),
    },
    {
      label: "Sonstiges",
      disabled: true, // temporary "fix" to prevent discount errors in create event
      content: (
        <Miscellaneous
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
          eventId={eventId ? eventId : "0"}
        />
      ),
    },
  ];

  const editELearningTabs = [
    {
      label: "Material",
      content: <Documents typeOfTrainingToBeCreated={typeOfTrainingToBeCreated} />,
    },
    {
      label: "Teilnehmer",
      content: (
        <Participants eventData={editEventState} trainingType={TrainingType.ELearning} />
      ),
      disabled: editEventState.status === EventStatus.NotVisible,
      disabledTooltip:
        'Der Reiter "Teilnehmer" ist nicht verfügbar, solange das E-Learning nicht sichtbar ist.',
    },
    {
      label: "Sonstiges",
      content: (
        <Miscellaneous
          typeOfTrainingToBeCreated={typeOfTrainingToBeCreated}
          eventId={eventId ? eventId : "0"}
        />
      ),
    },
  ];

  function getTabs() {
    switch (viewMode) {
      case "createEvent":
        if (typeOfTrainingToBeCreated === TrainingType.DefaultEvent) {
          return createEventTabs;
        } else if (typeOfTrainingToBeCreated === TrainingType.ELearning) {
          return elearningTabs;
        } else {
          return createEventTabs;
        }

      case "editEvent":
        if (typeOfTrainingToBeCreated !== TrainingType.ELearning) {
          return editEventTabs;
        } else {
          return editELearningTabs;
        }

      case "template":
        return templateTabs;
      default:
        return createEventTabs;
    }
  }

  return (
    <CustomTabs
      onTabChange={() => {
        setFieldValue("descriptionState", values.description); //Workaround for strange behavior, when ENTER/BACKSPACE in Editor
        handleTabChange(activeTab);
      }}
      activeTab={activeTab}
      tabs={[...defaultTabs, ...getTabs()]}
    />
  );
};

export default EventTabs;

// TODO no translation yet
